import type { ColumnPickerGroup } from "@fox1ck-org/ui";

import type { TKey } from "@/lib/i18n";

/**
 * What the package's ColumnPicker is handed, the same in the campaign list
 * (доска CampColumns) and in the report (доска ReportColumns): each column
 * by its short name with a quiet note on the right of what it counts
 * («люди», «все заходы», «из решённых»), grouped Трафик · Конверсии ·
 * Деньги · Свои. The goals' own counts are not one of the board's groups;
 * they stand last, apart from the conversions, so the two columns of the
 * picker keep the board's Трафик + Конверсии | Деньги + Свои. Pure.
 */

export type PickerGroupId = "traffic" | "conversions" | "money" | "custom" | "goals";
export const PICKER_GROUP_ORDER: readonly PickerGroupId[] = ["traffic", "conversions", "money", "custom", "goals"];

export interface PickerEntry {
  key: string;
  /** The short name — the header's word, never the registry's description. */
  label: string;
  group: PickerGroupId;
  hint?: string;
}

/**
 * The order the boards list the built-in columns in (ReportColumns, the
 * fuller of the two): the conversions lead with what decides — лиды, CR, исход,
 * апрув — and the money with spend and revenue. A key not named here (a goal,
 * a formula, a metric the registry grows) keeps the schema's place after them.
 */
const BOARD_ORDER: readonly string[] = [
  "visits", "clicks", "unique_clicks", "bots", "bot_share", "bots_pct", "lander_shows", "cta_clicks", "lp_ctr", "offer_visits",
  "leads", "cr", "outcome", "approve_rate", "conversions", "conversions_hold", "rejected", "trash", "lander_cr",
  "cost", "revenue", "revenue_hold", "revenue_in_transit", "epc", "cpc", "cpl", "cpa", "lander_epc", "profit", "roi", "roas",
];

function boardRank(key: string): number {
  const i = BOARD_ORDER.indexOf(key);
  return i < 0 ? BOARD_ORDER.length : i;
}

/** The entries as the picker's groups, in the board's order; an empty group is not drawn. Pure. */
export function pickerGroups(entries: readonly PickerEntry[], groupLabel: (g: PickerGroupId) => string): ColumnPickerGroup[] {
  return PICKER_GROUP_ORDER.flatMap((g) => {
    const columns = entries
      .filter((e) => e.group === g)
      .map((e, i) => ({ e, i }))
      .sort((a, b) => boardRank(a.e.key) - boardRank(b.e.key) || a.i - b.i)
      .map(({ e }) => ({ id: e.key, label: e.label, ...(e.hint ? { hint: e.hint } : {}) }));
    return columns.length > 0 ? [{ id: g, label: groupLabel(g), columns }] : [];
  });
}

/** The columns the board writes a note beside (доска CampColumns); the rest stand alone. */
const HINTS: Readonly<Record<string, TKey>> = {
  visits: "cols.hint.visits",
  clicks: "cols.hint.clicks",
  bots: "cols.hint.bots",
  lp_ctr: "cols.hint.lpCtr",
  cr: "cols.hint.cr",
  outcome: "cols.hint.outcome",
  approve_rate: "cols.hint.approveRate",
  revenue: "cols.hint.revenue",
};

/** A built-in column's note, or undefined. */
export function columnHint(t: (k: TKey) => string, key: string): string | undefined {
  const k = HINTS[key];
  return k ? t(k) : undefined;
}

/**
 * A workspace formula's note: its expression in the columns' own words —
 * «(доход − расход) / лиды» for `(revenue - cost) / leads`. Pure.
 */
export function formulaHint(expression: string, labelOf: (key: string) => string | undefined): string {
  return expression
    .replace(/[A-Za-z_][\w.]*/g, (k) => (labelOf(k) ?? k).toLocaleLowerCase("ru-RU"))
    .replace(/-/g, "−")
    .replace(/\*/g, "×");
}

/**
 * Which groups stand in which of the picker's two columns (доски CampColumns,
 * ReportColumns): Трафик + Конверсии | Деньги + Свои. The goals are not named,
 * so the package puts them at the end of the right column. Pure data.
 */
export const PICKER_LAYOUT: readonly [readonly PickerGroupId[], readonly PickerGroupId[]] = [
  ["traffic", "conversions"],
  ["money", "custom"],
];

/**
 * The next order after a tick or an untick: what stays keeps its place, and a
 * newly ticked column goes in beside its nearest shown neighbour of the same
 * group in the picker's order (after the one before it, else before the one
 * after it), so the row does not jump to the end of the table. With no shown
 * neighbour in its group it goes beside the nearest shown column of the
 * groups around it; with none at all, last. Pure.
 */
export function nextOrder(order: readonly string[], visibility: Readonly<Record<string, boolean>>, groups: readonly ColumnPickerGroup[]): string[] {
  const seq = groups.flatMap((g) => g.columns.map((c) => ({ id: c.id, group: g.id })));
  const next = order.filter((k) => visibility[k] !== false);
  for (const { id } of seq.filter((c) => visibility[c.id] === true && !next.includes(c.id))) insertBeside(next, id, seq);
  return next;
}

function insertBeside(order: string[], key: string, seq: readonly { id: string; group: string }[]): void {
  const i = seq.findIndex((c) => c.id === key);
  if (i < 0) {
    order.push(key);
    return;
  }
  const group = seq[i].group;
  const shown = (c: { id: string }) => order.includes(c.id);
  const before = seq.slice(0, i).reverse();
  const after = seq.slice(i + 1);
  const prev = before.find((c) => c.group === group && shown(c));
  if (prev) return void order.splice(order.indexOf(prev.id) + 1, 0, key);
  const nextInGroup = after.find((c) => c.group === group && shown(c));
  if (nextInGroup) return void order.splice(order.indexOf(nextInGroup.id), 0, key);
  const prevAny = before.find(shown);
  if (prevAny) return void order.splice(order.indexOf(prevAny.id) + 1, 0, key);
  const nextAny = after.find(shown);
  if (nextAny) return void order.splice(order.indexOf(nextAny.id), 0, key);
  order.push(key);
}
